import React from "react";

import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Typography, MenuItem } from "@mui/material";
import { pdf } from "@react-pdf/renderer";
import { makeStyles } from "tss-react/mui";

import client from "@/client";
import { graphql } from "@/gql";
import { Program } from "@/gql/graphql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { colors } from "@/theme/overrides/colors";
import { displayError } from "@/utils/errors";

import PDFDocuments from "./PDFDocuments";

const useStyles = makeStyles()({
  icon: {
    color: colors.primary,
    marginRight: 8,
    width: 21,
    height: 21,
  },
});

type Props = {
  programId: Program["id"];
  onClick?: () => void;
};

const pdfDownloadLinkMenuItemQuery = graphql(`
  query PDFDownloadLinkMenuItem($id: ID!) {
    program(id: $id) {
      id
      ...PDFDocuments
      startDate
      endDate
    }
  }
`);

const PDFDownloadLinkMenuItem: React.FC<Props> = ({ programId }) => {
  const { classes } = useStyles();
  const snackbar = useGlobalSnackbar();

  async function renderPDF() {
    try {
      const { data } = await client.query({
        query: pdfDownloadLinkMenuItemQuery,
        variables: { id: programId },
      });
      if (data.program == null) return;

      const fileName = `${data.program.name}_${data.program.startDate}-${data.program.endDate}.pdf`;
      const blob = await pdf(<PDFDocuments pdfData={data.program} />).toBlob();
      const link = document.createElement("a");
      link.download = fileName;
      link.href = URL.createObjectURL(blob);
      link.click();
    } catch (e) {
      snackbar.openSnackBar(
        displayError(e, "Unable to save as PDF. Please try again later.")
      );
    }
  }

  return (
    <MenuItem onClick={() => renderPDF()}>
      <PictureAsPdfIcon className={classes.icon} />
      <Typography variant="body2" color="textPrimary">
        Save as PDF
      </Typography>
    </MenuItem>
  );
};

export default PDFDownloadLinkMenuItem;
